/**
 * PageScaffold (Phase-2)
 *
 * Shared layout for Phase-2 module pages: title, optional subtitle, optional
 * action area (e.g. "Upload" button), and consistent loading/error states.
 * Keeps the new screens visually consistent with the existing design without
 * duplicating boilerplate.
 *
 * @author Civilhealth Development Team
 */
import React from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';

export interface PageScaffoldProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  onDismissError?: () => void;
  children?: React.ReactNode;
}

const PageScaffold: React.FC<PageScaffoldProps> = ({
  title,
  subtitle,
  icon,
  actionLabel,
  onAction,
  actionIcon,
  loading,
  error,
  onDismissError,
  children,
}) => {
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1A1A2E' }}>{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        {actionLabel && onAction && (
          <Button
            variant="contained"
            startIcon={actionIcon}
            onClick={onAction}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
              boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={onDismissError}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

export default PageScaffold;